import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { BlobService } from '../../../services/blob/blob.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, map, startWith } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AvatarPrimitive } from '../../../models/app/avatar-primitive';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
    selector: 'app-chip-selector',
    templateUrl: './chip-selector.component.html',
    styleUrls: ['./chip-selector.component.css'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => ChipSelectorComponent),
        multi: true
    }]
})

export class ChipSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {

    @Input() value: number[] = [];
    @Output() valueChange: EventEmitter<number[]> = new EventEmitter<number[]>();
    public onChange: any = () => { };
    public onTouched: any = () => { };

    public visible = true;
    public selectable = true;
    public removable = true;
    public addOnBlur = true;
    public separatorKeysCodes: number[] = [ENTER, COMMA];
    public itemControl = new FormControl();
    public filteredItems!: Observable<AvatarPrimitive[]>;
    public selectedItems: AvatarPrimitive[] = [];
    public chipCssClass: string = "";

    @ViewChild('itemInput', { static: false }) itemInput!: ElementRef<HTMLInputElement>;
    @ViewChild('auto', { static: false }) matAutocomplete!: MatAutocomplete;

    @Input() public label: string | undefined = undefined;
    @Input() public className: string | undefined = undefined;
    @Input() public availableItems: AvatarPrimitive[] = [];
    @Input() public selectedItemIds: number[] = [];
    @Input() public isMultiSelect: boolean | undefined = undefined;
    @Input() public idLinkPath: string | undefined = undefined;
    @Input() public disabled: boolean = false;
    @Output() public itemAdded = new EventEmitter<AvatarPrimitive>();
    @Output() public itemRemoved = new EventEmitter<AvatarPrimitive>();
    @Output() public itemClicked = new EventEmitter<number>();

    public constructor(public appService: AppService, public blobService: BlobService) { }

    public writeValue(value: any): void {

        this.value = value;
    }

    public registerOnChange(fn: any): void {

        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {

        this.onTouched = fn;
    }

    public setDisabledState?(isDisabled: boolean): void {

        //
    }

    public onInput(event: any): void {

        this.value = event.target.value;
        this.onChange(this.value);
        this.onTouched();
    }

    public ngOnInit(): void {

        if (this.isMultiSelect) {

            this.chipCssClass = "multiple-select-mode-input-chip";

        } else {

            this.chipCssClass = "single-select-mode-input-chip";
        }

        if (this.idLinkPath) {

            this.chipCssClass = this.chipCssClass + " clickable-chip";
        }
    }

    public ngAfterViewInit(): void {

        const s: string = "";
    }

    public ngOnChanges(changes: SimpleChanges) {

        if (changes["selectedItemIds"]) {

            const itemId = this.selectedItemIds[0];

            const selectedItems: AvatarPrimitive[] = this.availableItems.filter((i: AvatarPrimitive) => i.Id == itemId);

            if (selectedItems.length == 1) {

                this.selectedItems.push(selectedItems[0]);
            }

            this.filteredItems = this.itemControl.valueChanges.pipe(startWith(null), map((searchString: string | null) => searchString ? this._filter(searchString) : this.availableItems.slice()));
        }

        if (changes["value"]) {

            const itemIds: number[] = this.value;

            this.selectedItems = this.availableItems.filter((i: AvatarPrimitive) => itemIds.includes(i.Id));

            this.filteredItems = this.itemControl.valueChanges.pipe(startWith(null), map((searchString: string | null) => searchString ? this._filter(searchString) : this.availableItems.slice()));
        }

        if (changes["availableItems"]) {

            this.filteredItems = this.itemControl.valueChanges.pipe(startWith(null), map((searchString: string | null) => searchString ? this._filter(searchString) : this.availableItems.slice()));

            if (this.selectedItemIds.length == 1) { // remove or re-select the selected item in case it's name has changed

                const selectedItems: AvatarPrimitive[] = this.availableItems.filter((i: AvatarPrimitive) => i.Id == this.selectedItemIds[0])

                if (selectedItems && selectedItems.length == 1) {

                    this.selectedItems = []
                    this.selectedItems = selectedItems;

                } else {

                    this.selectedItemIds = [];
                    this.selectedItems = [];
                }
            }
        }
    }

    public add = (event: MatChipInputEvent): void => {

        if (!this.matAutocomplete.isOpen) { // add item only when MatAutocomplete is not open to make sure this does not conflict with OptionSelected Event

            const input = event.input;
            const value = event.value;

            if ((value || "").trim()) {

                var s: string = "";
                //this.selectedResources.push(value.trim());
                //this.selectedItems.push(value);
            }

            if (input) { // reset input value

                input.value = "";
            }

            this.itemControl.setValue(null);
        }
    }

    public remove(item: AvatarPrimitive): void {

        const index = this.selectedItems.indexOf(item);

        if (index >= 0) {

            this.selectedItems.splice(index, 1);
            this.selectedItemIds = this.selectedItems.map((i: AvatarPrimitive) => i.Id);
            this.writeValue(this.selectedItemIds);
            this.itemRemoved.emit(item);
        }
    }

    public clear(): void {

        this.selectedItems = [];
    }

    public selected(event: MatAutocompleteSelectedEvent): void { // resource selected

        if (!this.isMultiSelect) {

            this.selectedItems = [];
        }

        this.selectedItems.push(event.option.value);

        const item: AvatarPrimitive = event.option.value;

        this.itemInput.nativeElement.value = "";
        this.itemControl.setValue(null);
        this.selectedItemIds = this.selectedItems.map((i: AvatarPrimitive) => i.Id);
        this.writeValue(this.selectedItemIds);
        this.itemAdded.emit(item);
    }

    private _filter(searchString: any): AvatarPrimitive[] { // filter resource list based on user input 

        if (searchString instanceof AvatarPrimitive) {

            searchString = ""; // hacky fix to restore the available item list (after the user selects an item [of type avatarprimitive])
        }

        const filterValue: string = searchString.toLowerCase();
        //const result = this.availableItems.filter((item: AvatarPrimitive) => (item.Name.toLowerCase()).indexOf(filterValue) === 0); // suffix wildcard
        const result = this.availableItems.filter((item: AvatarPrimitive) => (item.Name.toLowerCase()).includes(filterValue)); // prefix and suffix wildcard

        return result;
    }

    public chipClick = (editGram: any): void => {

        if (this.idLinkPath) {

            const path: string = this.idLinkPath + "/" + editGram.Id;
            this.appService.navigateTo(path, "");

        } else {

            this.itemClicked.emit(editGram.Id);
        }
    }
}
